import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, formatCurrency, apiErrorMessage, reportBulkDelete, reportBulkVerify } from '../utils/formatters';

/**
 * Reconciliation tab state + handlers.
 *
 * This is the largest cluster inside AdminTransactionsPage.jsx — it
 * owns every piece of state and every network call that lives inside
 * the recon tab (including its many dialogs). The parent still
 * renders the dialogs; this hook just supplies the state + handlers
 * they read.
 *
 * External deps:
 *   • `token` — used for useCallback deps on fetchReconciliation
 *   • `headers` — auth header, pinned at page level
 *   • `fetchTransactions` — main-tab refresh after split
 *
 * Nothing here changes semantics vs the pre-refactor page.
 */
export function useReconciliation({ token, headers, fetchTransactions, onReconRefreshed }) {
  // ── Recon data + filters ──
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconInvestorFilter, setReconInvestorFilter] = useState('');
  const [reconIncludeUntagged, setReconIncludeUntagged] = useState(false);
  const [reconFromDate, setReconFromDate] = useState('');
  const [reconToDate, setReconToDate] = useState('');
  const [reconSearch, setReconSearch] = useState('');

  // ── Selection + expansion ──
  const [selectedRecordedTxn, setSelectedRecordedTxn] = useState(null);
  const [selectedBankTxn, setSelectedBankTxn] = useState(null);
  const [selectedReconRecorded, setSelectedReconRecorded] = useState(new Set());
  const [selectedReconBank, setSelectedReconBank] = useState(new Set());
  const [selectedMatched, setSelectedMatched] = useState(new Set());
  const [expandedMatched, setExpandedMatched] = useState(new Set());

  // ── Visibility toggles (defaults match parent) ──
  const [showMatched, setShowMatched] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showUnmatchedUploaded, setShowUnmatchedUploaded] = useState(true);
  const [showUnmatchedBank, setShowUnmatchedBank] = useState(true);
  const [suggestionBusyId, setSuggestionBusyId] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [bulkSuggestionLoading, setBulkSuggestionLoading] = useState(false);

  // ── Match confirmation + shared match/unmatch state ──
  const [matchConfirmOpen, setMatchConfirmOpen] = useState(false);
  const [matching, setMatching] = useState(false);
  const [reconBulkDeleting, setReconBulkDeleting] = useState(false);
  const [multiAllocLoading, setMultiAllocLoading] = useState(false);

  // ── Sort state per subtable ──
  const [sortRecon, setSortRecon] = useState({ key: 'recorded.transaction_date', dir: 'desc' });
  const [sortReconRecorded, setSortReconRecorded] = useState({ key: 'transaction_date', dir: 'desc' });
  const [sortReconBank, setSortReconBank] = useState({ key: 'posting_date', dir: 'desc' });

  // ── Inspect (bank side) ──
  const [inspectOpen, setInspectOpen] = useState(false);
  const [inspectBankId, setInspectBankId] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  // ── Inspect (recorded side) ──
  const [inspectRecOpen, setInspectRecOpen] = useState(false);
  const [inspectRecId, setInspectRecId] = useState(null);
  const [inspectRecData, setInspectRecData] = useState(null);
  const [inspectRecLoading, setInspectRecLoading] = useState(false);

  // ── Allocation dialog (single) ──
  const [allocationOpen, setAllocationOpen] = useState(false);
  const [allocationBankTxn, setAllocationBankTxn] = useState(null);
  const [allocationLoading, setAllocationLoading] = useState(false);

  // ── Reverse allocation (many banks → 1 recorded) ──
  const [reverseAllocOpen, setReverseAllocOpen] = useState(false);
  const [reverseAllocRecorded, setReverseAllocRecorded] = useState(null);
  const [reverseAllocLoading, setReverseAllocLoading] = useState(false);

  // ── Split transaction (1 recorded → N parts) ──
  const [splitTxnOpen, setSplitTxnOpen] = useState(false);
  const [splitTxnTarget, setSplitTxnTarget] = useState(null);
  const [splitTxnLoading, setSplitTxnLoading] = useState(false);

  // ── Bank-split (1 bank → N children) ──
  const [bankSplitOpen, setBankSplitOpen] = useState(false);
  const [bankSplitTarget, setBankSplitTarget] = useState(null);
  const [bankSplitParts, setBankSplitParts] = useState([]);
  const [bankSplitNotes, setBankSplitNotes] = useState('');
  const [bankSplitLoading, setBankSplitLoading] = useState(false);

  // ── Manage splits ──
  const [manageSplitsOpen, setManageSplitsOpen] = useState(false);
  const [splitParents, setSplitParents] = useState([]);
  const [splitParentsLoading, setSplitParentsLoading] = useState(false);
  const [splitDiagnostics, setSplitDiagnostics] = useState({});
  const [splitDiagLoadingId, setSplitDiagLoadingId] = useState(null);

  // ── Re-import Excel + Replay ──
  const [reimportOpen, setReimportOpen] = useState(false);
  const [reimportFile, setReimportFile] = useState(null);
  const [reimportLoading, setReimportLoading] = useState(false);
  const [reimportResult, setReimportResult] = useState(null);

  // ── Bulk reassign investor on bank txns ──
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignInvestor, setReassignInvestor] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);

  // ══ Selection toggles ══
  const toggleMatched = (id) => setSelectedMatched(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleReconRecorded = (id) => setSelectedReconRecorded(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleReconBank = (id) => setSelectedReconBank(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // `list` is the currently FILTERED set shown on screen (search/date
  // filters applied) — defaults to the full unmatched set only when no
  // filter is active, so "select all" never reaches past what's visible.
  const toggleAllReconRecorded = (list) => {
    const items = list || reconData?.unmatched_recorded || [];
    setSelectedReconRecorded(prev => (prev.size === items.length && items.length > 0)
      ? new Set()
      : new Set(items.map(t => t.id)));
  };
  const toggleAllReconBank = (list) => {
    const items = list || reconData?.unmatched_bank || [];
    setSelectedReconBank(prev => (prev.size === items.length && items.length > 0)
      ? new Set()
      : new Set(items.map(t => t.id)));
  };

  // ══ Fetch recon data ══
  const fetchReconciliation = useCallback(async (invId = '', fromDt = '', toDt = '', includeUntagged = null) => {
    setReconLoading(true);
    try {
      const params = new URLSearchParams();
      if (invId) params.set('investor_id', invId);
      if (fromDt) params.set('from_date', fromDt);
      if (toDt) params.set('to_date', toDt);
      const untagged = includeUntagged === null ? reconIncludeUntagged : includeUntagged;
      if (untagged) params.set('include_untagged_bank', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation${qs}`, { headers });
      setReconData(r.data);
      // Keep the investor picker's green ticks current as you reconcile
      // (background; the tick check takes ~1s and never blocks this view).
      if (onReconRefreshed) onReconRefreshed();
      setSelectedRecordedTxn(null);
      setSelectedBankTxn(null);
      // The bulk-checkbox selections (recorded/bank/matched) are keyed by
      // id against THIS list — a new fetch (investor/date filter change,
      // plain refresh, ...) replaces the list wholesale, so stale ids left
      // in these sets no longer correspond to anything on screen. Without
      // this, the sticky "Verify N Selected" bar keeps showing a leftover
      // count from a previous investor/filter even though nothing visible
      // is checked.
      setSelectedReconRecorded(new Set());
      setSelectedReconBank(new Set());
      setSelectedMatched(new Set());
    } catch (err) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setReconLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, reconIncludeUntagged]);

  // Helper — every "refresh recon after action" hits this with the
  // currently active filters. Matches the parent's original inlined logic.
  // No-ops when no investor is selected yet (reconInvestorFilter is '' or
  // 'all', which both mean "no investor picked" — 'all' is not a real
  // choice, includeAll={false} on the picker below) rather than firing the
  // unfiltered full-book load (previously hung the backend for minutes —
  // see fetchReconciliation's own comment above; a global action button
  // like Promote Reconciled Staging can legitimately be clicked before any
  // investor is selected, since it isn't investor-scoped itself).
  const refresh = () => {
    const invId = reconInvestorFilter === 'all' ? '' : reconInvestorFilter;
    if (!invId) return;
    return fetchReconciliation(invId, reconFromDate, reconToDate);
  };

  // ══ Inspect handlers ══
  const openInspect = async (bankId) => {
    setInspectBankId(bankId);
    setInspectOpen(true);
    setInspectData(null);
    setInspectLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/bank-allocations/${encodeURIComponent(bankId)}`, { headers });
      setInspectData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load allocations');
    } finally { setInspectLoading(false); }
  };

  const forceCleanBank = async () => {
    if (!inspectBankId) return;
    if (!window.confirm(`FORCE-CLEAN this bank transaction?\n\nThis deletes ALL allocation records pointing at this bank, even orphaned/stale ones, and resets every recorded txn that thought it was matched.\n\nUse this when normal unmatch doesn't free the bank.`)) return;
    setInspectLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/force-clean-bank`, { bank_transaction_id: inspectBankId }, { headers });
      toast.success(r.data.message || 'Force-cleaned');
      setInspectOpen(false);
      setInspectBankId(null);
      setInspectData(null);
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Force-clean failed'));
    } finally { setInspectLoading(false); }
  };

  const openInspectRecorded = async (txnId) => {
    setInspectRecId(txnId);
    setInspectRecOpen(true);
    setInspectRecData(null);
    setInspectRecLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/recorded-allocations/${encodeURIComponent(txnId)}`, { headers });
      setInspectRecData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load allocations');
    } finally { setInspectRecLoading(false); }
  };

  const deleteAllocationDoc = async (allocId) => {
    if (!allocId) return;
    if (!window.confirm(`Delete this allocation record?\n\nThe linked bank txn will be freed, and any recorded txn it was matched to returns to Unmatched.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/investor-transactions/allocations/${encodeURIComponent(allocId)}`, { headers });
      toast.success('Allocation reversed');
      if (inspectRecId) openInspectRecorded(inspectRecId);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete allocation');
    }
  };

  const forceCleanRecorded = async () => {
    if (!inspectRecId) return;
    if (!window.confirm(`FORCE-CLEAN this recorded transaction?\n\nThis removes it from EVERY allocation record (even orphaned/phantom ones) and resets it back to the Unmatched list.\n\nUse this when 'Already allocated' shows a balance you can't trace or unallocate.`)) return;
    setInspectRecLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/force-clean-recorded`, { transaction_id: inspectRecId }, { headers });
      toast.success(r.data.message || 'Force-cleaned');
      setInspectRecOpen(false);
      setInspectRecId(null);
      setInspectRecData(null);
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Force-clean failed'));
    } finally { setInspectRecLoading(false); }
  };

  // ══ Bulk actions ══
  const handleReassignBank = async () => {
    if (selectedReconBank.size === 0 || !reassignInvestor || reassignInvestor === 'all') {
      toast.error('Pick an investor first');
      return;
    }
    setReassignLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/bulk-reassign-investor`,
        { transaction_ids: [...selectedReconBank], investor_id: reassignInvestor },
        { headers },
      );
      toast.success(r.data.message || `Reassigned ${selectedReconBank.size} bank txn(s)`);
      setReassignOpen(false);
      setReassignInvestor('');
      setSelectedReconBank(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reassign failed');
    } finally {
      setReassignLoading(false);
    }
  };

  const handleReconBulkDeleteRecorded = async () => {
    if (selectedReconRecorded.size === 0) return;
    if (!window.confirm(`Delete ${selectedReconRecorded.size} recorded transaction(s)?`)) return;
    setReconBulkDeleting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-transactions/bulk-delete`, { ids: [...selectedReconRecorded] }, { headers });
      reportBulkDelete(toast, r.data);
      setSelectedReconRecorded(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally { setReconBulkDeleting(false); }
  };

  const handleReconBulkDeleteBank = async () => {
    if (selectedReconBank.size === 0) return;
    if (!window.confirm(`Delete ${selectedReconBank.size} bank transaction(s)?\n\nThis works for live Investec sync rows AND historical CSV uploads. Any allocation records pointing at them will also be removed.`)) return;
    setReconBulkDeleting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/bulk-delete-banks`,
        { transaction_ids: [...selectedReconBank] },
        { headers },
      );
      toast.success(r.data.message || `${selectedReconBank.size} bank transaction(s) deleted`);
      setSelectedReconBank(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally { setReconBulkDeleting(false); }
  };

  const handleReconBulkVerifyBank = async () => {
    if (selectedReconBank.size === 0) return;
    const reason = window.prompt(
      `Verify ${selectedReconBank.size} bank transaction(s) as 'no counterpart required'.\n\nReason (optional):`,
      'No investor counterpart - bank-side only',
    );
    if (reason === null) return;
    setReconBulkDeleting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/verify-no-match/bulk`,
        {
          transaction_ids: [...selectedReconBank],
          reason: reason || null,
        },
        { headers },
      );
      reportBulkVerify(toast, r.data);
      setSelectedReconBank(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk verify failed');
    } finally { setReconBulkDeleting(false); }
  };

  const handleReconBulkVerifyRecorded = async () => {
    if (selectedReconRecorded.size === 0) return;
    const reason = window.prompt(
      `Verify ${selectedReconRecorded.size} transaction(s) as 'no counterpart required'.\n\nReason (optional):`,
      'Same-day rebalancing - no bank movement',
    );
    if (reason === null) return;
    setReconBulkDeleting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/verify-no-match/bulk`,
        {
          transaction_ids: [...selectedReconRecorded],
          reason: reason || null,
        },
        { headers },
      );
      reportBulkVerify(toast, r.data);
      setSelectedReconRecorded(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk verify failed');
    } finally { setReconBulkDeleting(false); }
  };

  // Send a big selection in 500-row batches (no cap on how many you select —
  // user 2026-10-09). One request per batch keeps each call well inside the
  // proxy timeout; results are summed for a single toast.
  const _forceCleanInBatches = async (url, key, ids) => {
    let succeeded = 0, failed = 0;
    for (let i = 0; i < ids.length; i += 500) {
      // eslint-disable-next-line no-await-in-loop
      const r = await axios.post(url, { [key]: ids.slice(i, i + 500) }, { headers });
      succeeded += r.data.succeeded || 0;
      failed += r.data.failed || 0;
      if (r.data.failed) console.warn('force-clean failures', (r.data.results || []).filter(x => !x.ok));
      if (ids.length > 500) toast.info(`Force-cleaned ${Math.min(i + 500, ids.length)} of ${ids.length}…`);
    }
    return { succeeded, failed };
  };

  const handleReconBulkForceCleanBank = async () => {
    if (selectedReconBank.size === 0) return;
    if (!window.confirm(`FORCE-CLEAN ${selectedReconBank.size} bank transaction(s)?\n\nThis deletes ALL allocation records pointing at each one, even orphaned/stale ones, and resets every recorded txn that thought it was matched to them.\n\nUse this when normal unmatch doesn't free them.`)) return;
    setReconBulkDeleting(true);
    try {
      const r = await _forceCleanInBatches(
        `${API_URL}/api/admin/investor-transactions/reconciliation/force-clean-bank/bulk`,
        'bank_transaction_ids', [...selectedReconBank],
      );
      toast.success(`Force-cleaned ${r.succeeded} of ${selectedReconBank.size} bank transaction(s)`);
      if (r.failed > 0) toast.warning(`${r.failed} failed — check console`);
      setSelectedReconBank(new Set());
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Bulk force-clean failed'));
    } finally { setReconBulkDeleting(false); }
  };

  const handleReconBulkForceCleanRecorded = async () => {
    if (selectedReconRecorded.size === 0) return;
    if (!window.confirm(`FORCE-CLEAN ${selectedReconRecorded.size} recorded transaction(s)?\n\nThis removes each one from EVERY allocation record (even orphaned/phantom ones) and resets them back to the Unmatched list.\n\nUse this when 'Already allocated' shows a balance you can't trace or unallocate.`)) return;
    setReconBulkDeleting(true);
    try {
      const r = await _forceCleanInBatches(
        `${API_URL}/api/admin/investor-transactions/reconciliation/force-clean-recorded/bulk`,
        'transaction_ids', [...selectedReconRecorded],
      );
      toast.success(`Force-cleaned ${r.succeeded} of ${selectedReconRecorded.size} recorded transaction(s)`);
      if (r.failed > 0) toast.warning(`${r.failed} failed — check console`);
      setSelectedReconRecorded(new Set());
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Bulk force-clean failed'));
    } finally { setReconBulkDeleting(false); }
  };

  // ══ Allocation flows ══
  const handleOpenAllocation = (bankTxn) => {
    setAllocationBankTxn(bankTxn);
    setAllocationOpen(true);
  };

  const handleAllocationConfirm = async (payload) => {
    setAllocationLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/allocate-split`, payload, { headers });
      toast.success(res.data.message || `Allocated ${res.data.allocation_count} transaction(s)`);
      setAllocationOpen(false);
      setAllocationBankTxn(null);
      setSelectedBankTxn(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Allocation failed');
    } finally { setAllocationLoading(false); }
  };

  const runDedupe = async (kind) => {
    const inv = reconInvestorFilter && reconInvestorFilter !== 'all' ? reconInvestorFilter : null;
    const url = kind === 'uploaded'
      ? `${API_URL}/api/admin/investor-transactions/reconciliation/dedupe-uploaded`
      : `${API_URL}/api/admin/investor-transactions/reconciliation/dedupe-bank`;
    const label = kind === 'uploaded' ? 'uploaded' : 'bank';
    const rule = kind === 'uploaded'
      ? 'Rule: same date + same type + same amount = duplicate.'
      : 'Rule: same date + amount + description AND the same Investec transaction (same id, posting order and running balance) = duplicate. Genuinely separate same-day payments are kept.';
    try {
      const preview = await axios.post(url, { investor_id: inv, dry_run: true }, { headers });
      const p = preview.data || {};
      const rowsToDelete = p.rows_to_delete || 0;
      const review = p.possible_duplicates_to_review || [];
      if (review.length) {
        toast.warning(`${review.length} possible duplicate pair(s) can't be confirmed automatically (an older copy lacks Investec's posting order) — not deleted. Check them by hand.`, { duration: 15000 });
        console.warn('Dedupe: possible duplicates to review', review);
      }
      if (rowsToDelete === 0) {
        toast.message(`No duplicate ${label} rows found.`, {
          description: `Scanned ${p.scanned_rows || 0} ${label} rows across ${p.distinct_signatures || 0} distinct signatures.`,
          duration: 10000,
        });
        return;
      }
      const proceed = window.confirm(
        `Dedupe ${label} preview for ${inv || 'ALL investors'}:\n\n`
        + `${p.groups_with_duplicates || 0} duplicate group(s) — ${rowsToDelete} row(s) will be deleted.\n\n`
        + `${rule}\n`
        + `Allocated/verified rows are preserved as survivors.\n\n`
        + `Proceed?`,
      );
      if (!proceed) {
        toast.info('Dedupe cancelled.');
        return;
      }
      const r = await axios.post(url, { investor_id: inv, dry_run: false }, { headers });
      toast.success(r.data?.message || 'Dedupe complete');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dedupe failed');
    }
  };

  const runFixAllocatedCashflows = async () => {
    try {
      const preview = await axios.get(
        `${API_URL}/api/admin/investor-transactions/reconciliation/diagnose-allocated-cashflows`,
        { headers },
      );
      const p = preview.data || {};
      if (!p.gap_count) {
        toast.message('No affected rows found.', {
          description: 'Every allocated cashflow already has its reconciliation status set directly.',
          duration: 10000,
        });
        return;
      }
      const topInvestors = (p.by_investor || []).slice(0, 10)
        .map(([code, n]) => `${code}: ${n}`).join('\n');
      const proceed = window.confirm(
        `Found ${p.gap_count} allocated cashflow row(s) totalling R ${Number(p.total_amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })} `
        + `across ${p.investors_affected} investor(s) that are correctly allocated but missing their reconciliation status - `
        + `invisible to the General Ledger, statements and dashboards until fixed.\n\n`
        + `Top affected:\n${topInvestors}\n\n`
        + `This only sets reconciliation_status/matched_bank_txn_id on rows an existing, intact allocation `
        + `already covers - it does not create or change any allocation. Proceed?`,
      );
      if (!proceed) {
        toast.info('Backfill cancelled.');
        return;
      }
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/fix-allocated-cashflows`,
        {},
        { headers },
      );
      toast.success(`Fixed ${r.data?.fixed || 0} row(s)` + (r.data?.skipped_count ? `, ${r.data.skipped_count} skipped (see console)` : ''));
      if (r.data?.skipped_count) console.warn('fix-allocated-cashflows skipped:', r.data.skipped);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    }
  };

  const runPromoteConfirmedStaging = async () => {
    try {
      const preview = await axios.get(
        `${API_URL}/api/admin/investor-transactions/reconciliation/diagnose-unpromoted-staging`,
        { headers },
      );
      const p = preview.data || {};
      if (!p.total_confirmed_but_unpromoted) {
        toast.message('Nothing to promote.', {
          description: 'Every staging bank transaction that was already reconciled has been committed to the ledger.',
          duration: 10000,
        });
        return;
      }
      const proceed = window.confirm(
        `Found ${p.total_confirmed_but_unpromoted} staging bank transaction(s) totalling R ${Number(p.total_amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })} `
        + `already reconciled (matched/verified) but never committed to the real ledger - invisible to the GL/cash balance until fixed.\n\n`
        + (p.blocked_needs_classification_first
            ? `${p.blocked_needs_classification_first} of these still need an investor linked and/or a classified type first (see Import & Approve) and will be skipped.\n\n`
            : '')
        + `This does not change any match/verify decision you already made - it just makes it count. Proceed?`,
      );
      if (!proceed) {
        toast.info('Backfill cancelled.');
        return;
      }
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/promote-confirmed-staging`,
        {},
        { headers },
      );
      toast.success(`Promoted ${r.data?.promoted_count || 0} row(s)` + (r.data?.blocked_count ? `, ${r.data.blocked_count} still need classification (see console)` : ''));
      if (r.data?.blocked_count) console.warn('promote-confirmed-staging blocked:', r.data.blocked);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    }
  };

  const handleAllocateMultiBank = async () => {
    if (!selectedRecordedTxn || selectedReconBank.size === 0) return;
    const recordedAmount = Math.abs(Number(selectedRecordedTxn.amount) || 0);
    const alreadyOnRecorded = Number(selectedRecordedTxn.allocated_amount) || 0;
    let recordedRemaining = +(recordedAmount - alreadyOnRecorded).toFixed(2);
    if (recordedRemaining <= 0.01) {
      toast.error('Recorded transaction already fully allocated');
      return;
    }
    const banksToAllocate = (reconData?.unmatched_bank || []).filter(b => selectedReconBank.has(b.id));
    if (banksToAllocate.length === 0) return;

    const totalBankAvail = banksToAllocate.reduce((s, b) => {
      const rem = b.remaining_balance != null ? Number(b.remaining_balance) : Math.abs(Number(b.amount) || 0);
      return s + rem;
    }, 0);

    const confirmMsg = `Allocate ${banksToAllocate.length} bank txn(s) (total R${totalBankAvail.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}) to recorded R${recordedAmount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} (remaining R${recordedRemaining.toLocaleString('en-ZA', { minimumFractionDigits: 2 })})?\n\nBanks will be consumed in order; the last bank may be partially used if it exceeds the remaining recorded amount.`;
    if (!window.confirm(confirmMsg)) return;

    setMultiAllocLoading(true);
    let totalAllocated = 0;
    let lastError = null;

    for (const bank of banksToAllocate) {
      if (recordedRemaining <= 0.01) break;
      const bankRem = bank.remaining_balance != null ? Number(bank.remaining_balance) : Math.abs(Number(bank.amount) || 0);
      if (bankRem <= 0.01) continue;
      const bankSign = (Number(bank.amount) || 0) < 0 ? -1 : 1;
      const allocAbs = +(Math.min(bankRem, recordedRemaining)).toFixed(2);
      const allocAmount = +(allocAbs * bankSign).toFixed(2);
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/allocate-split`, {
          bank_transaction_id: bank.id,
          bank_amount: Number(bank.amount) || 0,
          allocations: [{ transaction_id: selectedRecordedTxn.id, amount: allocAmount }],
          write_off_amount: 0,
        }, { headers });
        totalAllocated += allocAbs;
        recordedRemaining = +(recordedRemaining - allocAbs).toFixed(2);
      } catch (err) {
        lastError = err;
        break;
      }
    }

    if (lastError) {
      toast.error(`Allocation failed after R${totalAllocated.toFixed(2)}: ${lastError.response?.data?.detail || lastError.message}`);
    } else {
      toast.success(`Allocated R${totalAllocated.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} across ${banksToAllocate.length} bank txn(s)`);
    }
    setSelectedReconBank(new Set());
    setSelectedRecordedTxn(null);
    refresh();
    setMultiAllocLoading(false);
  };

  // ══ Reverse allocation ══
  const handleOpenReverseAllocation = (recorded) => {
    setReverseAllocRecorded(recorded);
    setReverseAllocOpen(true);
  };

  const handleReverseAllocationConfirm = async (entries) => {
    if (!reverseAllocRecorded || !entries || entries.length === 0) return;
    setReverseAllocLoading(true);
    let success = 0;
    let lastError = null;
    let totalAllocated = 0;

    for (const e of entries) {
      const bank = (reconData?.unmatched_bank || []).find(b => b.id === e.bank_transaction_id);
      if (!bank) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/allocate-split`, {
          bank_transaction_id: e.bank_transaction_id,
          bank_amount: Number(bank.amount) || 0,
          allocations: [{
            transaction_id: reverseAllocRecorded.id,
            amount: e.amount,
            effective_date: e.effective_date || null,
          }],
          write_off_amount: 0,
        }, { headers });
        success += 1;
        totalAllocated += Math.abs(e.amount);
      } catch (err) {
        lastError = err;
        break;
      }
    }

    if (lastError) {
      const detail = lastError.response?.data?.detail || lastError.message || 'unknown error';
      toast.error(`Allocation failed after ${success} bank(s): ${detail}`, { duration: 8000 });
    } else {
      toast.success(`Allocated R${totalAllocated.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} across ${success} bank txn(s)`);
    }
    setReverseAllocOpen(false);
    setReverseAllocRecorded(null);
    setSelectedRecordedTxn(null);
    setSelectedReconBank(new Set());
    refresh();
    setReverseAllocLoading(false);
  };

  const handleUnallocateBank = async (recordedId, bankTxnId) => {
    if (!recordedId || !bankTxnId) return;
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/unallocate-bank`, {
        transaction_id: recordedId,
        bank_transaction_id: bankTxnId,
      }, { headers });
      toast.success('Bank unallocated from recorded');
      const updated = await axios.get(
        `${API_URL}/api/admin/investor-transactions/reconciliation${reconInvestorFilter && reconInvestorFilter !== 'all' ? `?investor_id=${reconInvestorFilter}` : ''}`,
        { headers },
      );
      setReconData(updated.data);
      const fresh = [
        ...(updated.data?.unmatched_recorded || []),
        ...((updated.data?.matched || []).map(m => m.recorded)),
      ].find(r => r?.id === recordedId);
      if (fresh) setReverseAllocRecorded(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unallocate');
    }
  };

  const handleWriteOffRecorded = async (recordedId, amount, reason) => {
    if (!recordedId || !amount) return;  // signed, as entered: negative closes an over-allocation
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/write-off-recorded`, {
        transaction_id: recordedId,
        write_off_amount: amount,
        reason: reason || null,
      }, { headers });
      toast.success(`Wrote off R${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`);
      setReverseAllocOpen(false);
      setReverseAllocRecorded(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Write-off failed');
    }
  };

  // ══ Split transaction (recorded → parts) ══
  const handleOpenSplit = (recorded) => {
    setSplitTxnTarget(recorded);
    setSplitTxnOpen(true);
  };

  const handleConfirmSplit = async (payload) => {
    if (!splitTxnTarget) return;
    setSplitTxnLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/investor-transactions/${splitTxnTarget.id}/split`,
        payload,
        { headers },
      );
      toast.success(res.data.message || 'Transaction split');
      setSplitTxnOpen(false);
      setSplitTxnTarget(null);
      refresh();
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to split transaction');
    } finally {
      setSplitTxnLoading(false);
    }
  };

  // ══ Bank-split ══
  const handleOpenBankSplit = (bank) => {
    setBankSplitTarget(bank);
    setBankSplitParts([
      { amount: '', description: '' },
      { amount: '', description: '' },
    ]);
    setBankSplitNotes('');
    setBankSplitOpen(true);
  };

  const handleConfirmBankSplit = async () => {
    if (!bankSplitTarget) return;
    const parentAmt = Number(bankSplitTarget.amount || 0);
    const parts = bankSplitParts
      .map(p => ({ amount: Number(p.amount), description: p.description?.trim() || null }))
      .filter(p => !isNaN(p.amount) && p.amount !== 0);
    if (parts.length < 2) {
      toast.error('Provide at least 2 non-zero splits');
      return;
    }
    const parentSign = parentAmt > 0 ? 1 : -1;
    if (parts.some(p => (p.amount > 0 ? 1 : -1) !== parentSign)) {
      toast.error(`All splits must be same sign as parent (${parentAmt >= 0 ? 'positive' : 'negative'})`);
      return;
    }
    const total = parts.reduce((s, p) => s + p.amount, 0);
    if (Math.abs(total - parentAmt) > 0.01) {
      toast.error(`Split total ${formatCurrency(total)} ≠ parent ${formatCurrency(parentAmt)} (max R0.01 drift)`);
      return;
    }
    setBankSplitLoading(true);
    try {
      const assignTo = reconInvestorFilter && reconInvestorFilter !== 'all'
        ? reconInvestorFilter
        : null;
      const res = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/split-bank`,
        {
          bank_id: bankSplitTarget.id,
          splits: parts,
          notes: bankSplitNotes.trim() || null,
          assign_to_investor_id: assignTo,
        },
        { headers },
      );
      toast.success(res.data.message || 'Bank txn split');
      setBankSplitOpen(false);
      setBankSplitTarget(null);
      setBankSplitParts([]);
      setBankSplitNotes('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to split bank txn');
    } finally {
      setBankSplitLoading(false);
    }
  };

  const handleUnsplitBank = async (bankId) => {
    if (!window.confirm('Unsplit this bank row? All children will be deleted and the parent restored to the unmatched pool.')) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/unsplit-bank`,
        { bank_id: bankId },
        { headers },
      );
      toast.success(res.data.message || 'Bank txn unsplit');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unsplit');
    }
  };

  // ══ Manage splits ══
  const openManageSplits = async () => {
    setManageSplitsOpen(true);
    setSplitParentsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/investor-transactions/reconciliation/split-parents`,
        { headers },
      );
      setSplitParents(res.data?.split_parents || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load split parents');
    } finally {
      setSplitParentsLoading(false);
    }
  };

  const runSplitDiagnostics = async (parentId) => {
    setSplitDiagLoadingId(parentId);
    try {
      const parentRow = splitParents.find(sp => sp.id === parentId);
      const filter = (reconInvestorFilter && reconInvestorFilter !== 'all')
        ? reconInvestorFilter
        : (parentRow?.investor_id || '');
      const params = new URLSearchParams();
      if (filter) params.set('investor_id', filter);
      const url = `${API_URL}/api/admin/investor-transactions/reconciliation/split-diagnostics/${parentId}${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, { headers });
      setSplitDiagnostics(prev => ({ ...prev, [parentId]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostics failed');
    } finally {
      setSplitDiagLoadingId(null);
    }
  };

  const unsplitFromManageDialog = async (parentId) => {
    if (!window.confirm('Unsplit this bank row? All children will be deleted and the parent restored to the unmatched pool.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/unsplit-bank`,
        { bank_id: parentId },
        { headers },
      );
      toast.success('Unsplit complete');
      setSplitParents(prev => prev.filter(p => p.id !== parentId));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unsplit');
    }
  };

  const reassignSplitChildren = async (parentId) => {
    if (!reconInvestorFilter || reconInvestorFilter === 'all') {
      toast.error('Select an investor on the recon page first — the children will be reassigned to that investor.');
      return;
    }
    if (!window.confirm(`Reassign every child of this split to the currently-selected investor?\n\nThe children will immediately appear on that investor's recon page.`)) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/reassign-split-children`,
        { parent_id: parentId, assign_to_investor_id: reconInvestorFilter },
        { headers },
      );
      toast.success(res.data?.message || 'Children reassigned');
      await runSplitDiagnostics(parentId);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reassign failed');
    }
  };

  // ══ Suggestions ══
  const acceptSuggestion = async (suggestion) => {
    const bankId = suggestion?.bank?.id;
    const recIds = (suggestion?.recorded || []).map(r => r.id).filter(Boolean);
    if (!bankId || !recIds.length) return;
    const key = `${bankId}::${recIds.join(',')}`;
    setSuggestionBusyId(key);
    try {
      await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/accept-suggestion`,
        { bank_transaction_id: bankId, recorded_ids: recIds },
        { headers },
      );
      toast.success('Suggestion accepted — allocation created.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Accept failed');
    } finally {
      setSuggestionBusyId(null);
    }
  };

  const rejectSuggestion = async (suggestion) => {
    const bankId = suggestion?.bank?.id;
    const recIds = (suggestion?.recorded || []).map(r => r.id).filter(Boolean);
    if (!bankId || !recIds.length) return;
    const key = `${bankId}::${recIds.join(',')}`;
    setSuggestionBusyId(key);
    try {
      await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/reject-suggestion`,
        { bank_transaction_id: bankId, recorded_ids: recIds },
        { headers },
      );
      toast.success('Suggestion rejected — it will not re-appear.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    } finally {
      setSuggestionBusyId(null);
    }
  };

  // ── Suggestion multi-select + bulk accept/reject ──
  const toggleSuggestionSelected = (key) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAllSuggestionsSelected = (allKeys) => {
    setSelectedSuggestions(prev => (
      prev.size === allKeys.length ? new Set() : new Set(allKeys)
    ));
  };

  const _suggestionsByKey = () => {
    const map = new Map();
    for (const s of (reconData?.suggestions || [])) {
      const bankId = s?.bank?.id;
      const recIds = (s?.recorded || []).map(r => r.id).filter(Boolean);
      if (!bankId || !recIds.length) continue;
      map.set(`${bankId}::${recIds.join(',')}`, { bank_transaction_id: bankId, recorded_ids: recIds });
    }
    return map;
  };

  const bulkAcceptSuggestions = async () => {
    const byKey = _suggestionsByKey();
    const items = [...selectedSuggestions].map(k => byKey.get(k)).filter(Boolean);
    if (!items.length) return;
    setBulkSuggestionLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/accept-suggestion/bulk`,
        { suggestions: items },
        { headers },
      );
      toast.success(r.data?.message || `Accepted ${r.data?.accepted_count || 0} suggestion(s)`);
      if (r.data?.failed_count > 0) {
        toast.warning(`${r.data.failed_count} failed — they stayed in the list`);
      }
      setSelectedSuggestions(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk accept failed');
    } finally {
      setBulkSuggestionLoading(false);
    }
  };

  const bulkRejectSuggestions = async () => {
    const byKey = _suggestionsByKey();
    const items = [...selectedSuggestions].map(k => byKey.get(k)).filter(Boolean);
    if (!items.length) return;
    if (!window.confirm(`Reject ${items.length} suggestion(s)? They will not re-appear.`)) return;
    setBulkSuggestionLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/reject-suggestion/bulk`,
        { suggestions: items },
        { headers },
      );
      toast.success(r.data?.message || `Rejected ${r.data?.rejected_count || 0} suggestion(s)`);
      setSelectedSuggestions(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk reject failed');
    } finally {
      setBulkSuggestionLoading(false);
    }
  };

  // ══ Verify / unmatch / match ══
  const handleVerifyNoMatch = async (txn, kind) => {
    if (!txn) return;
    const reason = window.prompt(
      `Reason for verifying this ${kind} as-is (no match required)?`,
      kind === 'bank' ? 'Cash adjustment' : 'Non-cash adjustment',
    );
    if (reason === null) return;
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/verify-no-match`, {
        transaction_id: txn.id,
        reason: reason || null,
      }, { headers });
      toast.success('Verified - moved to matched section');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify');
    }
  };

  const handleResplitMatched = async (recorded) => {
    if (!recorded) return;
    if (!window.confirm(`Reverse current allocations on this transaction and re-split?\n\nRecorded: ${recorded.investor_name || recorded.investor_id} - R${Math.abs(Number(recorded.amount) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/resplit-recorded/${recorded.id}`, {}, { headers });
      toast.success('Existing allocations reversed - choose new banks');
      await refresh();
      setReverseAllocRecorded({
        ...recorded,
        allocated_amount: 0,
        remaining_to_allocate: Math.abs(Number(recorded.amount) || 0),
      });
      setReverseAllocOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse allocations');
    }
  };

  const handleDeleteAllocation = async (allocId) => {
    if (!window.confirm('Reverse this allocation? All linked transactions will be unmatched.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/investor-transactions/allocations/${allocId}`, { headers });
      toast.success('Allocation reversed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse allocation');
    }
  };

  const handleManualMatch = async () => {
    if (!selectedRecordedTxn || !selectedBankTxn) return;
    setMatching(true);
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/manual-match`, {
        transaction_id: selectedRecordedTxn.id,
        bank_transaction_id: selectedBankTxn.id,
      }, { headers });
      toast.success('Transactions matched successfully');
      setSelectedRecordedTxn(null);
      setSelectedBankTxn(null);
      setMatchConfirmOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match transactions');
    } finally {
      setMatching(false);
    }
  };

  const handleUnmatch = async (txnId, bankId = null) => {
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/unmatch`, {
        transaction_id: txnId,
        bank_transaction_id: bankId,
      }, { headers });
      toast.success('Match removed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unmatch');
    }
  };

  const handleUndoVerifyNoMatch = async (id) => {
    if (!id) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/investor-transactions/reconciliation/verify-no-match/undo/${encodeURIComponent(id)}`,
        {}, { headers },
      );
      toast.success('Verification removed — back in Unmatched list');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo verification');
    }
  };

  const handleReconBulkUnmatch = async () => {
    if (selectedMatched.size === 0) return;
    const matchedById = new Map();
    (reconData?.matched || []).forEach(m => {
      if (m.recorded?.id) matchedById.set(m.recorded.id, m);
      if (m.bank?.id) matchedById.set(m.bank.id, m);
    });
    if (!window.confirm(`Unmatch ${selectedMatched.size} transaction(s)? They will return to the Unmatched lists.`)) return;
    setReconBulkDeleting(true);
    let ok = 0;
    const fails = [];
    for (const id of selectedMatched) {
      const m = matchedById.get(id);
      const isVerified = m?.match_quality === 'verified_no_match';
      const recordedId = m?.recorded?.id || '';
      const bankId = m?.bank?.id || null;
      try {
        if (isVerified) {
          const undoId = recordedId || bankId;
          if (!undoId) throw new Error('No id to undo');
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/verify-no-match/undo/${encodeURIComponent(undoId)}`, {}, { headers });
        } else {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/unmatch`, { transaction_id: recordedId || id, bank_transaction_id: bankId }, { headers });
        }
        ok += 1;
      } catch (err) {
        fails.push({ id, error: err.response?.data?.detail || err.message });
      }
    }
    toast.success(`Unmatched ${ok} of ${selectedMatched.size}`);
    if (fails.length) {
      // eslint-disable-next-line no-console
      console.warn('Unmatch failures:', fails);
      toast.warning(`${fails.length} failed — check console`);
    }
    setSelectedMatched(new Set());
    refresh();
    setReconBulkDeleting(false);
  };

  // ══ Re-import Excel + Replay ══
  const handleReimportExcel = async () => {
    if (!reimportFile) { toast.error('Pick an Excel file first'); return; }
    setReimportLoading(true);
    setReimportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', reimportFile);
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/reimport-excel-replay`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
      );
      setReimportResult(r.data);
      if (r.data.status === 'failed') {
        toast.error(`Import failed: ${r.data.error || 'unknown error'}`);
      } else {
        toast.success(r.data.message);
        refresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-import failed');
    } finally {
      setReimportLoading(false);
    }
  };

  return {
    // Recon data + filters
    reconData, setReconData,
    reconLoading,
    reconInvestorFilter, setReconInvestorFilter,
    reconIncludeUntagged, setReconIncludeUntagged,
    reconFromDate, setReconFromDate,
    reconToDate, setReconToDate,
    reconSearch, setReconSearch,

    // Selection + expansion
    selectedRecordedTxn, setSelectedRecordedTxn,
    selectedBankTxn, setSelectedBankTxn,
    selectedReconRecorded, setSelectedReconRecorded,
    selectedReconBank, setSelectedReconBank,
    selectedMatched, setSelectedMatched,
    expandedMatched, setExpandedMatched,

    // Visibility
    showMatched, setShowMatched,
    showSuggestions, setShowSuggestions,
    showUnmatchedUploaded, setShowUnmatchedUploaded,
    showUnmatchedBank, setShowUnmatchedBank,
    suggestionBusyId,

    // Match/unmatch UI state
    matchConfirmOpen, setMatchConfirmOpen,
    matching,
    reconBulkDeleting,
    multiAllocLoading,

    // Sort
    sortRecon, setSortRecon,
    sortReconRecorded, setSortReconRecorded,
    sortReconBank, setSortReconBank,

    // Fetch
    fetchReconciliation,

    // Toggles
    toggleMatched,
    toggleReconRecorded, toggleReconBank,
    toggleAllReconRecorded, toggleAllReconBank,

    // Inspect (bank)
    inspectOpen, setInspectOpen,
    inspectBankId, setInspectBankId,
    inspectData, setInspectData,
    inspectLoading,
    openInspect, forceCleanBank,

    // Inspect (recorded)
    inspectRecOpen, setInspectRecOpen,
    inspectRecId, setInspectRecId,
    inspectRecData, setInspectRecData,
    inspectRecLoading,
    openInspectRecorded, deleteAllocationDoc, forceCleanRecorded,

    // Bulk
    handleReassignBank,
    handleReconBulkDeleteRecorded, handleReconBulkDeleteBank,
    handleReconBulkVerifyBank, handleReconBulkVerifyRecorded,
    handleReconBulkForceCleanBank, handleReconBulkForceCleanRecorded,
    handleReconBulkUnmatch,

    // Allocation
    allocationOpen, setAllocationOpen,
    allocationBankTxn, setAllocationBankTxn,
    allocationLoading,
    handleOpenAllocation, handleAllocationConfirm,
    runDedupe,
    runFixAllocatedCashflows,
    runPromoteConfirmedStaging,
    handleAllocateMultiBank,

    // Reverse alloc
    reverseAllocOpen, setReverseAllocOpen,
    reverseAllocRecorded, setReverseAllocRecorded,
    reverseAllocLoading,
    handleOpenReverseAllocation, handleReverseAllocationConfirm,
    handleUnallocateBank, handleWriteOffRecorded,

    // Split txn
    splitTxnOpen, setSplitTxnOpen,
    splitTxnTarget, setSplitTxnTarget,
    splitTxnLoading,
    handleOpenSplit, handleConfirmSplit,

    // Bank-split
    bankSplitOpen, setBankSplitOpen,
    bankSplitTarget, setBankSplitTarget,
    bankSplitParts, setBankSplitParts,
    bankSplitNotes, setBankSplitNotes,
    bankSplitLoading,
    handleOpenBankSplit, handleConfirmBankSplit, handleUnsplitBank,

    // Manage splits
    manageSplitsOpen, setManageSplitsOpen,
    splitParents, setSplitParents,
    splitParentsLoading,
    splitDiagnostics, setSplitDiagnostics,
    splitDiagLoadingId,
    openManageSplits, runSplitDiagnostics,
    reassignSplitChildren, unsplitFromManageDialog,
    refresh,

    // Suggestions
    acceptSuggestion, rejectSuggestion,
    selectedSuggestions, setSelectedSuggestions,
    toggleSuggestionSelected, toggleAllSuggestionsSelected,
    bulkSuggestionLoading, bulkAcceptSuggestions, bulkRejectSuggestions,

    // Verify / match
    handleVerifyNoMatch, handleResplitMatched,
    handleDeleteAllocation,
    handleManualMatch, handleUnmatch, handleUndoVerifyNoMatch,

    // Re-import
    reimportOpen, setReimportOpen,
    reimportFile, setReimportFile,
    reimportLoading,
    reimportResult, setReimportResult,
    handleReimportExcel,

    // Reassign
    reassignOpen, setReassignOpen,
    reassignInvestor, setReassignInvestor,
    reassignLoading,
  };
}
